// Item 11 Path 4 — competitor fingerprint dictionary.
//
// Each profile describes one launch competitor: the expected export column
// headers, a pre-filled CrewBit column map, plain-language export
// instructions, and the dbSourceValue the row is tagged with on insert.
//
// SOURCE-OF-TRUTH NOTE: The five `dbSourceValue` strings MUST match the
// values in the clients_source_check constraint from migration 127. Adding
// a new competitor means updating both in the same commit.
//
// Column-header guesses below are best-effort approximations of what each
// competitor's CSV export produces at the time this was written. They're
// used only as fingerprints — if a user's actual file has different
// headers, detection falls through to Path 3's generic auto-detection
// without error (see SpreadsheetPath integration). The five competitors
// are listed in alphabetical order.

import type { LucideIcon } from 'lucide-react';
import {
  Briefcase,
  CreditCard,
  Hammer,
  Scissors,
  Sparkles,
} from 'lucide-react';

export type CompetitorId =
  | 'booksy'
  | 'housecall_pro'
  | 'jobber'
  | 'square'
  | 'vagaro';

export type CompetitorDbSource =
  | 'import_booksy'
  | 'import_housecall_pro'
  | 'import_jobber'
  | 'import_square'
  | 'import_vagaro';

// Matches the CrewBitField union in ../spreadsheet/types.ts so the profile
// can seed MappingStep's mappings directly. Kept as a local type here so
// this file has no cross-directory dependency on the spreadsheet path.
export type CrewBitImportField =
  | 'name'
  | 'first_name'
  | 'last_name'
  | 'phone'
  | 'email'
  | 'ignore';

export interface CompetitorImportProfile {
  id: CompetitorId;
  displayName: string;
  hint: string;
  dbSourceValue: CompetitorDbSource;
  // Expected column headers in the competitor's export. Best-effort; used
  // only for ≥3-match fingerprint detection. Case-insensitive compare.
  fingerprintColumns: string[];
  // Pre-filled mapping from competitor header → CrewBit field. Any header
  // not listed here or not found in the uploaded file falls back to
  // 'ignore'. Names are resolved via the Rules below.
  columnMap: Record<string, CrewBitImportField>;
  exportInstructionsTitle: string;
  exportInstructionsSteps: string[];
  icon: LucideIcon;
}

// Rules for multi-column name resolution (First + Last) and phone preference
// (Cell Phone preferred over Home Phone) are enforced by the consumer
// (CompetitorPath / MappingStep) using these hints:
export const NAME_COMBINE_RULES: Record<
  CompetitorId,
  { first?: string; last?: string }
> = {
  booksy: { first: 'First Name', last: 'Last Name' },
  vagaro: { first: 'First Name', last: 'Last Name' },
  housecall_pro: {},
  jobber: {},
  square: {},
};

// Vagaro exports two phone columns. The consumer picks Cell Phone first and
// falls back to Home Phone only if Cell Phone is empty for a given row.
export const PHONE_FALLBACK_ORDER: Record<CompetitorId, string[]> = {
  booksy: ['Phone'],
  housecall_pro: ['Primary Phone'],
  jobber: ['Phone'],
  square: ['Phone Number'],
  vagaro: ['Cell Phone', 'Home Phone'],
};

export const COMPETITOR_PROFILES: CompetitorImportProfile[] = [
  {
    id: 'booksy',
    displayName: 'Booksy',
    hint: 'I use Booksy for bookings',
    dbSourceValue: 'import_booksy',
    fingerprintColumns: ['First Name', 'Last Name', 'Email', 'Phone', 'Date of Birth'],
    columnMap: {
      'First Name': 'first_name',
      'Last Name': 'last_name',
      'Email': 'email',
      'Phone': 'phone',
      'Date of Birth': 'ignore',
    },
    exportInstructionsTitle: 'Get your Booksy client list',
    exportInstructionsSteps: [
      'Sign in to Booksy on the web.',
      'Open Customers from the left menu.',
      'Click Export and choose CSV.',
      'Save the file somewhere you can find it.',
      'Come back here and upload it.',
    ],
    icon: Scissors,
  },
  {
    id: 'housecall_pro',
    displayName: 'Housecall Pro',
    hint: 'I use Housecall Pro for jobs',
    dbSourceValue: 'import_housecall_pro',
    fingerprintColumns: [
      'Customer Name',
      'Primary Phone',
      'Primary Email',
      'Street Address',
    ],
    columnMap: {
      'Customer Name': 'name',
      'Primary Phone': 'phone',
      'Primary Email': 'email',
      'Street Address': 'ignore',
    },
    exportInstructionsTitle: 'Get your Housecall Pro client list',
    exportInstructionsSteps: [
      'Sign in to Housecall Pro on the web.',
      'Open the Customers page.',
      'Click the Export button at the top right.',
      'Choose CSV and save the file.',
      'Come back here and upload it.',
    ],
    icon: Hammer,
  },
  {
    id: 'jobber',
    displayName: 'Jobber',
    hint: 'I use Jobber for field service',
    dbSourceValue: 'import_jobber',
    fingerprintColumns: ['Client Name', 'Phone', 'Email', 'Address'],
    columnMap: {
      'Client Name': 'name',
      'Phone': 'phone',
      'Email': 'email',
      'Address': 'ignore',
    },
    exportInstructionsTitle: 'Get your Jobber client list',
    exportInstructionsSteps: [
      'Sign in to Jobber on the web.',
      'Open Clients from the sidebar.',
      'Click More Actions, then Export Clients.',
      'Save the CSV file.',
      'Come back here and upload it.',
    ],
    icon: Briefcase,
  },
  {
    id: 'square',
    displayName: 'Square',
    hint: 'I use Square for payments',
    dbSourceValue: 'import_square',
    fingerprintColumns: [
      'Customer Name',
      'Email Address',
      'Phone Number',
      'Customer Since',
    ],
    columnMap: {
      'Customer Name': 'name',
      'Email Address': 'email',
      'Phone Number': 'phone',
      'Customer Since': 'ignore',
    },
    exportInstructionsTitle: 'Get your Square customer list',
    exportInstructionsSteps: [
      'Sign in to Square Dashboard on the web.',
      'Open the Customers directory.',
      'Click Import/Export, then Export customers.',
      'Save the CSV file.',
      'Come back here and upload it.',
    ],
    icon: CreditCard,
  },
  {
    id: 'vagaro',
    displayName: 'Vagaro',
    hint: 'I use Vagaro for appointments',
    dbSourceValue: 'import_vagaro',
    fingerprintColumns: [
      'First Name',
      'Last Name',
      'Email',
      'Cell Phone',
      'Home Phone',
    ],
    // Cell Phone is mapped to 'phone'; Home Phone is 'ignore'. Rationale:
    // MappingStep expects one phone column and the user can override Home
    // Phone → phone from the mapping screen if their list is home-only.
    // See phase 4 deviation note.
    columnMap: {
      'First Name': 'first_name',
      'Last Name': 'last_name',
      'Email': 'email',
      'Cell Phone': 'phone',
      'Home Phone': 'ignore',
    },
    exportInstructionsTitle: 'Get your Vagaro client list',
    exportInstructionsSteps: [
      'Sign in to Vagaro on the web.',
      'Open Customers from the menu.',
      'Click Export and choose CSV.',
      'Save the file.',
      'Come back here and upload it.',
    ],
    icon: Sparkles,
  },
];

// Case-insensitive, trimmed header compare. Returns the first profile whose
// fingerprint has ≥3 matching headers in the uploaded file, or null.
export function detectCompetitorFromHeaders(
  headers: string[],
): CompetitorImportProfile | null {
  const normalized = new Set(
    headers.map(h => h.trim().toLowerCase()).filter(h => h.length > 0),
  );
  for (const profile of COMPETITOR_PROFILES) {
    let hits = 0;
    for (const col of profile.fingerprintColumns) {
      if (normalized.has(col.trim().toLowerCase())) hits += 1;
      if (hits >= 3) return profile;
    }
  }
  return null;
}
